import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import OnyxApi from "../../components/OnyxApi";

function ContractCreate({
  chosenComponents,
  handleRestart,
  mileage,
  selectedProgram,
  vehicle,
  vin,
}) {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const userToken = user?.usertoken;

  const calculateRetailPrice = () => {
    if (!selectedProgram) return "";

    // Start with base program price
    let total = selectedProgram.retail_price || selectedProgram.cost || 0;

    // Add price for each chosen component
    if (
      selectedProgram.products &&
      chosenComponents &&
      chosenComponents.length > 0
    ) {
      selectedProgram.products.forEach((product) => {
        if (chosenComponents.includes(product.id)) {
          total += product.retail_price || product.cost || 0;
        }
      });
    }

    return total.toString();
  };

  const [formData, setFormData] = useState({
    buyer: {
      name: "",
      first_name: "",
      last_name: "",
      address: {
        line1: "",
        line2: "",
        city: "",
        state: "",
        postal_code: "",
        country_id: 1,
      },
      phone: "",
      email: "",
    },
    cobuyer: {
      first_name: "",
      last_name: "",
      name: "",
      phone: "",
      email: "",
      address: {
        line1: "",
        line2: "",
        city: "",
        postal_code: "",
        state: "",
        country_id: 1,
      },
    },
    program: {
      sale_price: calculateRetailPrice(),
    },
    vehicle: {
      price: "",
      msrp: "",
    },
    sale_date: (() => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    })(),
    lienholder: {
      name: "",
      address: {
        line1: "",
        line2: "",
        city: "",
        postal_code: "",
        state: "",
        country_id: 1,
      },
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasCobuyer, setHasCobuyer] = useState(false);
  const [hasLienholder, setHasLienholder] = useState(false);
  const [buyerType, setBuyerType] = useState("individual");
  const [cobuyerType, setCobuyerType] = useState("individual");
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (!userToken) {
      navigate("/", { replace: true });
      return;
    }
    if (!vehicle?.vin) {
      navigate("/vehicle-submission", { replace: true });
      return;
    }
    if (!selectedProgram) {
      navigate("/recommendations", { replace: true });
      return;
    }
  }, [userToken, vehicle, selectedProgram, navigate]);

  if (!vehicle?.vin || !selectedProgram) {
    return null;
  }

  const handleInputChange = (section, field, value, isAddress = false) => {
    setFormData((prev) => ({
      ...prev,
      [section]: isAddress
        ? {
            ...prev[section],
            address: {
              ...prev[section].address,
              [field]: value,
            },
          }
        : {
            ...prev[section],
            [field]: value,
          },
    }));
  };

  const handleBuyerNameChange = (firstName, lastName) => {
    const fullName = `${firstName} ${lastName}`.trim();
    setFormData((prev) => ({
      ...prev,
      buyer: {
        ...prev.buyer,
        first_name: firstName,
        last_name: lastName,
        name: fullName,
      },
    }));
  };

  const handleCobuyerNameChange = (firstName, lastName) => {
    const fullName = `${firstName} ${lastName}`.trim();
    setFormData((prev) => ({
      ...prev,
      cobuyer: {
        ...prev.cobuyer,
        first_name: firstName,
        last_name: lastName,
        name: fullName,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError(null);

    try {
      // Prepare buyer data based on type
      const buyerData = { ...formData.buyer };
      if (buyerType === "individual") {
        // For individuals, don't send the name field
        delete buyerData.name;
      } else {
        // For companies, don't send first_name and last_name
        delete buyerData.first_name;
        delete buyerData.last_name;
      }

      // Only include products for Per Component programs
      const isPerComponentProgram = selectedProgram.name.includes("Per");
      const programData = {
        id: selectedProgram.id,
        sale_price: parseFloat(formData.program.sale_price),
      };

      if (isPerComponentProgram && chosenComponents?.length > 0) {
        programData.products = chosenComponents;
      }

      const contractData = {
        buyer: buyerData,
        program: programData,
        vehicle: {
          vin: vin,
          mileage: parseInt(mileage),
          price: parseFloat(formData.vehicle.price),
          msrp: parseFloat(formData.vehicle.msrp),
        },
        sale_date: formData.sale_date,
        surcharges: [],
      };

      if (hasCobuyer) {
        // Prepare cobuyer data based on type
        const cobuyerData = { ...formData.cobuyer };
        if (cobuyerType === "individual") {
          // For individuals, don't send the name field
          delete cobuyerData.name;
        } else {
          // For companies, don't send first_name and last_name
          delete cobuyerData.first_name;
          delete cobuyerData.last_name;
        }
        contractData.cobuyer = cobuyerData;
      }

      if (hasLienholder) {
        contractData.lienholder = formData.lienholder;
      }

      const response = await OnyxApi("contractAdd", contractData, userToken);

      // Navigate to contract signing page with the response data
      navigate("/contract-signing", {
        state: { contractData: response.data },
        replace: true,
      });
    } catch (error) {
      console.error("Error creating contract:", error);

      // Extract error message and details from the API response
      if (error.response && error.response.data) {
        setApiError(error.response.data);
      } else {
        setApiError({
          message: "Error creating contract. Please try again.",
          errors: {},
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate("/disclosure", { replace: true });
  };

  return (
    <div className="contract-create-page">
      <div className="container">
        <div className="row pt-4">
          <div className="col-12">
            <h2>Create Contract</h2>
            <p>
              Complete the form below to create your contract for the{" "}
              {selectedProgram.name} plan on your {vehicle.year} {vehicle.make}{" "}
              {vehicle.model} (VIN: {vehicle.vin}).
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Error Display */}
          {apiError && (
            <div className="row">
              <div className="col-12">
                <div
                  className="alert alert-danger alert-dismissible"
                  role="alert"
                >
                  <strong>Error:</strong> {apiError.message}
                  {apiError.errors &&
                    Object.keys(apiError.errors).length > 0 && (
                      <ul className="mb-0 mt-2">
                        {Object.entries(apiError.errors).map(
                          ([field, messages]) => (
                            <li key={field}>
                              <strong>{field}:</strong>{" "}
                              {Array.isArray(messages)
                                ? messages.join(", ")
                                : messages}
                            </li>
                          )
                        )}
                      </ul>
                    )}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setApiError(null)}
                    aria-label="Close"
                  ></button>
                </div>
              </div>
            </div>
          )}

          {/* Buyer Information */}
          <div className="row">
            <div className="col-12">
              <h4>Buyer Information</h4>
            </div>
            <div className="col-12 mb-3">
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="buyerType"
                  id="buyerIndividual"
                  value="individual"
                  checked={buyerType === "individual"}
                  onChange={(e) => setBuyerType(e.target.value)}
                />
                <label
                  className="form-check-label ps-2"
                  htmlFor="buyerIndividual"
                >
                  Individual
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="buyerType"
                  id="buyerCompany"
                  value="company"
                  checked={buyerType === "company"}
                  onChange={(e) => setBuyerType(e.target.value)}
                />
                <label className="form-check-label ps-2" htmlFor="buyerCompany">
                  Company
                </label>
              </div>
            </div>
            {buyerType === "individual" ? (
              <>
                <div className="col-md-6">
                  <label className="form-label">First Name*</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.buyer.first_name}
                    onChange={(e) =>
                      handleBuyerNameChange(
                        e.target.value,
                        formData.buyer.last_name
                      )
                    }
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Last Name*</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.buyer.last_name}
                    onChange={(e) =>
                      handleBuyerNameChange(
                        formData.buyer.first_name,
                        e.target.value
                      )
                    }
                    required
                  />
                </div>
              </>
            ) : (
              <div className="col-md-12">
                <label className="form-label">Company Name*</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.buyer.name}
                  onChange={(e) =>
                    handleInputChange("buyer", "name", e.target.value)
                  }
                  required
                />
              </div>
            )}
            <div className="col-md-6">
              <label className="form-label">Phone*</label>
              <input
                type="tel"
                className="form-control"
                value={formData.buyer.phone}
                onChange={(e) =>
                  handleInputChange("buyer", "phone", e.target.value)
                }
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Email*</label>
              <input
                type="email"
                className="form-control"
                value={formData.buyer.email}
                onChange={(e) =>
                  handleInputChange("buyer", "email", e.target.value)
                }
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Address Line 1*</label>
              <input
                type="text"
                className="form-control"
                value={formData.buyer.address.line1}
                onChange={(e) =>
                  handleInputChange("buyer", "line1", e.target.value, true)
                }
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Address Line 2</label>
              <input
                type="text"
                className="form-control"
                value={formData.buyer.address.line2}
                onChange={(e) =>
                  handleInputChange("buyer", "line2", e.target.value, true)
                }
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">City*</label>
              <input
                type="text"
                className="form-control"
                value={formData.buyer.address.city}
                onChange={(e) =>
                  handleInputChange("buyer", "city", e.target.value, true)
                }
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">State*</label>
              <input
                type="text"
                className="form-control"
                value={formData.buyer.address.state}
                onChange={(e) =>
                  handleInputChange("buyer", "state", e.target.value, true)
                }
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Postal Code*</label>
              <input
                type="text"
                className="form-control"
                value={formData.buyer.address.postal_code}
                onChange={(e) =>
                  handleInputChange(
                    "buyer",
                    "postal_code",
                    e.target.value,
                    true
                  )
                }
                required
              />
            </div>
          </div>

          {/* Cobuyer Section */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="hasCobuyer"
                  checked={hasCobuyer}
                  onChange={(e) => setHasCobuyer(e.target.checked)}
                />
                <label className="form-check-label ps-2" htmlFor="hasCobuyer">
                  <h4>Add Cobuyer</h4>
                </label>
              </div>
            </div>
          </div>

          {hasCobuyer && (
            <div className="row">
              <div className="col-12 mb-3">
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cobuyerType"
                    id="cobuyerIndividual"
                    value="individual"
                    checked={cobuyerType === "individual"}
                    onChange={(e) => setCobuyerType(e.target.value)}
                  />
                  <label
                    className="form-check-label ps-2"
                    htmlFor="cobuyerIndividual"
                  >
                    Individual
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cobuyerType"
                    id="cobuyerCompany"
                    value="company"
                    checked={cobuyerType === "company"}
                    onChange={(e) => setCobuyerType(e.target.value)}
                  />
                  <label
                    className="form-check-label ps-2"
                    htmlFor="cobuyerCompany"
                  >
                    Company
                  </label>
                </div>
              </div>
              {cobuyerType === "individual" ? (
                <>
                  <div className="col-md-6">
                    <label className="form-label">First Name*</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.cobuyer.first_name}
                      onChange={(e) =>
                        handleCobuyerNameChange(
                          e.target.value,
                          formData.cobuyer.last_name
                        )
                      }
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Last Name*</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.cobuyer.last_name}
                      onChange={(e) =>
                        handleCobuyerNameChange(
                          formData.cobuyer.first_name,
                          e.target.value
                        )
                      }
                      required
                    />
                  </div>
                </>
              ) : (
                <div className="col-md-12">
                  <label className="form-label">Company Name*</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.cobuyer.name}
                    onChange={(e) =>
                      handleInputChange("cobuyer", "name", e.target.value)
                    }
                    required
                  />
                </div>
              )}
              <div className="col-md-6">
                <label className="form-label">Phone*</label>
                <input
                  type="tel"
                  className="form-control"
                  value={formData.cobuyer.phone}
                  onChange={(e) =>
                    handleInputChange("cobuyer", "phone", e.target.value)
                  }
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Email*</label>
                <input
                  type="email"
                  className="form-control"
                  value={formData.cobuyer.email}
                  onChange={(e) =>
                    handleInputChange("cobuyer", "email", e.target.value)
                  }
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Address Line 1*</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.cobuyer.address.line1}
                  onChange={(e) =>
                    handleInputChange("cobuyer", "line1", e.target.value, true)
                  }
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Address Line 2</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.cobuyer.address.line2}
                  onChange={(e) =>
                    handleInputChange("cobuyer", "line2", e.target.value, true)
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">City*</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.cobuyer.address.city}
                  onChange={(e) =>
                    handleInputChange("cobuyer", "city", e.target.value, true)
                  }
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">State*</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.cobuyer.address.state}
                  onChange={(e) =>
                    handleInputChange("cobuyer", "state", e.target.value, true)
                  }
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Postal Code*</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.cobuyer.address.postal_code}
                  onChange={(e) =>
                    handleInputChange(
                      "cobuyer",
                      "postal_code",
                      e.target.value,
                      true
                    )
                  }
                  required
                />
              </div>
            </div>
          )}

          {/* Program and Vehicle Information */}
          <div className="row mt-4">
            <div className="col-12">
              <h4>Sale Information</h4>
            </div>
            <div className="col-md-6">
              <label className="form-label">Sale Price*</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={formData.program.sale_price}
                onChange={(e) =>
                  handleInputChange("program", "sale_price", e.target.value)
                }
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Sale Date*</label>
              <input
                type="date"
                className="form-control"
                value={formData.sale_date}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    sale_date: e.target.value,
                  }))
                }
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Vehicle Price*</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={formData.vehicle.price}
                onChange={(e) =>
                  handleInputChange("vehicle", "price", e.target.value)
                }
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Vehicle MSRP*</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={formData.vehicle.msrp}
                onChange={(e) =>
                  handleInputChange("vehicle", "msrp", e.target.value)
                }
                required
              />
            </div>
          </div>

          {/* Lienholder Section */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="hasLienholder"
                  checked={hasLienholder}
                  onChange={(e) => setHasLienholder(e.target.checked)}
                />
                <label
                  className="form-check-label ps-2"
                  htmlFor="hasLienholder"
                >
                  <h4>Add Lienholder</h4>
                </label>
              </div>
            </div>
          </div>

          {hasLienholder && (
            <div className="row">
              <div className="col-md-6">
                <label className="form-label">Lienholder Name*</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.lienholder.name}
                  onChange={(e) =>
                    handleInputChange("lienholder", "name", e.target.value)
                  }
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Address Line 1*</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.lienholder.address.line1}
                  onChange={(e) =>
                    handleInputChange(
                      "lienholder",
                      "line1",
                      e.target.value,
                      true
                    )
                  }
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Address Line 2</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.lienholder.address.line2}
                  onChange={(e) =>
                    handleInputChange(
                      "lienholder",
                      "line2",
                      e.target.value,
                      true
                    )
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">City*</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.lienholder.address.city}
                  onChange={(e) =>
                    handleInputChange(
                      "lienholder",
                      "city",
                      e.target.value,
                      true
                    )
                  }
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">State*</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.lienholder.address.state}
                  onChange={(e) =>
                    handleInputChange(
                      "lienholder",
                      "state",
                      e.target.value,
                      true
                    )
                  }
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Postal Code*</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.lienholder.address.postal_code}
                  onChange={(e) =>
                    handleInputChange(
                      "lienholder",
                      "postal_code",
                      e.target.value,
                      true
                    )
                  }
                  required
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="row my-4">
            <div className="col-12">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Back to Disclosure
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating Contract..." : "Create Contract"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ContractCreate;
